import api from '../lib/api';
import { toArray } from '../lib/utils';
import type {
  CalendarProfessional,
  CalendarServiceType,
  CalendarSlot,
  CalendarBooking,
  SlotCreationParams,
} from '../types/calendar';

class CalendarService {
  // Professionals
  async getProfessionals(): Promise<CalendarProfessional[]> {
    const response = await api.get('/calendar/professionals');
    return toArray(response.data);
  }

  async createProfessional(data: Partial<CalendarProfessional>): Promise<CalendarProfessional> {
    const response = await api.post('/calendar/professionals', data);
    return response.data;
  }

  async getProfessional(id: number): Promise<CalendarProfessional> {
    const response = await api.get(`/calendar/professionals/${id}`);
    return response.data;
  }

  async updateProfessional(
    id: number,
    data: Partial<CalendarProfessional>
  ): Promise<CalendarProfessional> {
    const response = await api.put(`/calendar/professionals/${id}`, data);
    return response.data;
  }

  async deleteProfessional(id: number): Promise<void> {
    await api.delete(`/calendar/professionals/${id}`);
  }

  // Service Types
  async getServiceTypes(professionalId?: number): Promise<CalendarServiceType[]> {
    const params = professionalId ? { professional_id: professionalId } : undefined;
    const response = await api.get('/calendar/service-types', { params });
    return toArray(response.data);
  }

  async createServiceType(data: Partial<CalendarServiceType>): Promise<CalendarServiceType> {
    const response = await api.post('/calendar/service-types', data);
    return response.data;
  }

  async getServiceType(id: number): Promise<CalendarServiceType> {
    const response = await api.get(`/calendar/service-types/${id}`);
    return response.data;
  }

  async updateServiceType(
    id: number,
    data: Partial<CalendarServiceType>
  ): Promise<CalendarServiceType> {
    const response = await api.put(`/calendar/service-types/${id}`, data);
    return response.data;
  }

  async deleteServiceType(id: number): Promise<void> {
    await api.delete(`/calendar/service-types/${id}`);
  }

  // Slots
  async getSlots(params?: {
    professional_id?: number;
    date_start?: string;
    date_end?: string;
    status?: string;
    available_only?: boolean;
  }): Promise<CalendarSlot[]> {
    // Ensure dates are in YYYY-MM-DD format without timezone conversion
    const formattedParams = { ...params };
    if (formattedParams.date_start) {
      formattedParams.date_start = formattedParams.date_start.split('T')[0];
    }
    if (formattedParams.date_end) {
      formattedParams.date_end = formattedParams.date_end.split('T')[0];
    }

    const response = await api.get('/calendar/slots', { params: formattedParams });
    return toArray(response.data);
  }

  async createSlot(data: Partial<CalendarSlot>): Promise<CalendarSlot> {
    // Ensure date is in YYYY-MM-DD format without timezone conversion
    const formattedData = { ...data };
    if (formattedData.date) {
      formattedData.date = formattedData.date.split('T')[0];
    }

    const response = await api.post('/calendar/slots', formattedData);
    return response.data;
  }

  async createMultipleSlots(params: SlotCreationParams): Promise<CalendarSlot[]> {
    // Ensure date is in YYYY-MM-DD format without timezone conversion
    const formattedParams = { ...params };
    if (formattedParams.date) {
      formattedParams.date = formattedParams.date.split('T')[0];
    }

    // This is a frontend utility function that creates multiple slots
    // by making multiple API calls to the createSlot endpoint
    const {
      professional_id,
      date,
      start_time,
      end_time,
      slot_duration,
      break_between = 0,
    } = formattedParams;

    // Parse start and end times
    const [startHour, startMinute] = start_time.split(':').map(Number);
    const [endHour, endMinute] = end_time.split(':').map(Number);

    const startDate = new Date();
    startDate.setHours(startHour, startMinute, 0, 0);

    const endDate = new Date();
    endDate.setHours(endHour, endMinute, 0, 0);

    const slots: CalendarSlot[] = [];
    let currentTime = new Date(startDate);

    while (currentTime < endDate) {
      const slotStartTime = `${currentTime.getHours().toString().padStart(2, '0')}:${currentTime
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;

      // Calculate slot end time
      const slotEndTime = new Date(currentTime);
      slotEndTime.setMinutes(slotEndTime.getMinutes() + slot_duration);

      // If slot end time exceeds the overall end time, break
      if (slotEndTime > endDate) {
        break;
      }

      const slotEndTimeStr = `${slotEndTime.getHours().toString().padStart(2, '0')}:${slotEndTime
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;

      // Create the slot
      const slotData = {
        professional_id,
        date,
        start_time: slotStartTime,
        end_time: slotEndTimeStr,
        status: 'available' as const,
      };

      try {
        const createdSlot = await this.createSlot(slotData);
        slots.push(createdSlot);
      } catch (error) {
        console.error('Error creating slot:', error);
        // Continue with the next slot even if one fails
      }

      // Move to the next slot start time (including break)
      currentTime = new Date(slotEndTime);
      currentTime.setMinutes(currentTime.getMinutes() + break_between);
    }

    return slots;
  }

  async getSlot(id: number): Promise<CalendarSlot> {
    const response = await api.get(`/calendar/slots/${id}`);
    return response.data;
  }

  async updateSlot(id: number, data: Partial<CalendarSlot>): Promise<CalendarSlot> {
    // Ensure date is in YYYY-MM-DD format without timezone conversion
    const formattedData = { ...data };
    if (formattedData.date) {
      formattedData.date = formattedData.date.split('T')[0];
    }

    const response = await api.put(`/calendar/slots/${id}`, formattedData);
    return response.data;
  }

  async deleteSlot(id: number): Promise<void> {
    await api.delete(`/calendar/slots/${id}`);
  }

  // Bookings
  async getBookings(params?: {
    status?: string;
    date_start?: string;
    date_end?: string;
    professional_id?: number;
    service_type_id?: number;
    contact_id?: number;
    page?: number;
    limit?: number;
  }): Promise<{
    data: CalendarBooking[];
    pagination: {
      total: number;
      per_page: number;
      current_page: number;
      last_page: number;
    };
  }> {
    // Ensure dates are in YYYY-MM-DD format without timezone conversion
    const formattedParams = { ...params };
    if (formattedParams.date_start) {
      formattedParams.date_start = formattedParams.date_start.split('T')[0];
    }
    if (formattedParams.date_end) {
      formattedParams.date_end = formattedParams.date_end.split('T')[0];
    }

    const response = await api.get('/calendar/bookings', { params: formattedParams });
    return response.data;
  }

  async createBooking(data: {
    client_name: string;
    service_type_id: number;
    slot_ids: number[];
    status: 'booked' | 'confirmed';
    contact_id?: number;
  }): Promise<CalendarBooking> {
    const response = await api.post('/calendar/bookings', data);
    return response.data;
  }

  async getBooking(id: number): Promise<CalendarBooking> {
    const response = await api.get(`/calendar/bookings/${id}`);
    return response.data;
  }

  async updateBookingStatus(
    id: number,
    status: 'booked' | 'confirmed' | 'canceled'
  ): Promise<CalendarBooking> {
    const response = await api.put(`/calendar/bookings/${id}/status`, { status });
    return response.data;
  }
}

export default new CalendarService();
